import React, { useState } from 'react';
import { VoucherTransfer } from 'services/ItineraryManagerApi/types';
import Editor from 'pureUi/Editor';
import { SlideToggle } from 'ui/SlideToggle';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { useModal } from 'hooks/useModal';
import { VoucherAddUploadModal } from './VoucherAddUploadModal';
import { CircleIconButton } from 'ui/CircleIconButton';
import FluidButton from 'ui/FluidButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import classNames from 'classnames';
import { ProductEditUnderFold } from './common';

interface IVoucherTransferEditProps {
  transfers: VoucherTransfer[];
  voucherUuid: string;
  onVisibilityToggle: (transferUuid: string, isVisible: boolean) => void;
  onDescriptionChange: (transferUuid: string, description: string) => void;
  onMoreTextChange: (transferUuid: string, moreText: string) => void;
  onAddUpload: (transferUuid: string, uploadData: any) => void;
  onRemoveUpload: (transferUuid: string, uploadIndex: number) => void;
}

export const VoucherTransferEdit: React.FC<IVoucherTransferEditProps> = ({
  transfers,
  voucherUuid,
  onVisibilityToggle,
  onDescriptionChange,
  onMoreTextChange,
  onAddUpload,
  onRemoveUpload,
}) => {
  const [openTransfers, setOpenTransfers] = useState<string[]>([]);
  const addUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
    transferUuid: string;
  }>();

  if (transfers.length === 0) return null;

  const handleToggle = (transferUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenTransfers([...openTransfers, transferUuid]);
    } else {
      setOpenTransfers(openTransfers.filter(uuid => uuid !== transferUuid));
    }
  };

  return (
    <div className="mb-8">
      <span className="font-pt-sans font-bold text-[17px] text-black mb-4 block">Transfers</span>
      <div>
        {transfers.map(transfer => (
          <AccordionStackable
            key={transfer.uuid}
            isOpen={openTransfers.includes(transfer.uuid)}
            onToggle={isOpen => handleToggle(transfer.uuid, isOpen)}
            renderSummary={({ isOpen }) => (
              <span className="flex items-center justify-between w-full pr-4">
                <span
                  className={classNames('text-base font-pt-sans', {
                    'font-bold': isOpen,
                    'font-medium': !isOpen,
                  })}
                >
                  {transfer.title}
                </span>
                <SlideToggle
                  id={`toggle-transfer-${transfer.uuid}`}
                  checked={transfer.isVisible}
                  onActivate={() => onVisibilityToggle(transfer.uuid, true)}
                  onDeactivate={() => onVisibilityToggle(transfer.uuid, false)}
                  labelClassName={!transfer.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                  buttonClassName={!transfer.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                  onClick={e => e.stopPropagation()}
                />
              </span>
            )}
          >
            <div className="space-y-4">
              <ProductEditUnderFold product={transfer} />

              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                <Editor
                  handleEditorChange={content => onDescriptionChange(transfer.uuid, content)}
                  text={transfer.description || ''}
                  options={[]}
                  placeHolder="Write transfer description..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>
              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">More Text</p>
                <Editor
                  handleEditorChange={content => onMoreTextChange(transfer.uuid, content)}
                  text={transfer.moreText || ''}
                  options={[]}
                  placeHolder="Write additional transfer information..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>

              {/* Existing uploads */}
              {transfer.uploads.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-pt-sans text-gray-100 mb-2">Attachments</p>
                  <div className="flex gap-2 flex-wrap">
                    {transfer.uploads.map((upload, uploadIdx) => (
                      <div key={uploadIdx} className="relative">
                        <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                          <span className="w-full line-clamp-3">{upload.displayName}</span>
                        </span>
                        <CircleIconButton
                          className="absolute top-2 right-2"
                          IconComponent={XIcon}
                          iconSize="sm"
                          type="secondary"
                          onClick={() => onRemoveUpload(transfer.uuid, uploadIdx)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add attachment button */}
              <div className="mt-4">
                <FluidButton
                  onClick={async () => {
                    const result = await addUploadModalOps.requestConfirmation({
                      uploadType: 'file',
                      transferUuid: transfer.uuid,
                    });
                    if (result) {
                      onAddUpload(transfer.uuid, result);
                    }
                  }}
                  type="secondary"
                >
                  Attach File
                </FluidButton>
              </div>
            </div>
          </AccordionStackable>
        ))}
      </div>

      {/* Upload Modal */}
      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={addUploadModalOps.isOpen}
        onClose={addUploadModalOps.handleClose}
        uploadType={addUploadModalOps.modalData?.uploadType || 'file'}
        handleConfirm={addUploadModalOps.handleConfirm}
        modalTitle="Add File to Transfer"
        productType="transfer"
      />
    </div>
  );
};
